import { useStore } from '@/domain/state'
import { toAbsoluteExpiryMs } from '@/features/card/utils/toAbsoluteExpiryMs'
import { createTrackingId, withTrackingId } from '@/utils/trackedFetch'

let _cardWalletAddress: string | undefined

export function setCardTrackingWallet(address: string | undefined): void {
  _cardWalletAddress = address
}

export function getCardTrackingId(): string {
  return _cardWalletAddress ?? createTrackingId()
}

const isCardAPIEndpoint = (url: string): boolean => {
  return url.includes('/vendor/cards') || url.includes('/v1/vendor/cards')
}

const LOCAL_CARD_API_HOSTNAMES = new Set(['localhost', '127.0.0.1', '::1', '[::1]', 'host.docker.internal'])

function getCardAPIRequestURL(url: string): string {
  const parsedURL = new URL(url)
  const isLocalCardAPI = LOCAL_CARD_API_HOSTNAMES.has(parsedURL.hostname) || parsedURL.hostname.endsWith('.localhost')

  if (!isLocalCardAPI) {
    return url
  }

  return `/api/card/proxy${parsedURL.pathname}${parsedURL.search}`
}

let _refreshPromise: Promise<{ accessToken: string; tokenType: string }> | null = null

/**
 * Get fresh valid tokens - either from store if valid, or refresh and return new ones.
 * Serialized: concurrent callers share a single in-flight refresh to avoid races.
 */
async function getFreshTokens(): Promise<{ accessToken: string; tokenType: string }> {
  const store = useStore.getState()
  const cardAuth = store.cardAuth

  if (cardAuth.hasValidTokens() && !cardAuth.isTokenNearExpiry(60)) {
    return {
      accessToken: cardAuth.accessToken!,
      tokenType: cardAuth.tokenType!,
    }
  }

  if (_refreshPromise) {
    return _refreshPromise
  }

  _refreshPromise = (async () => {
    try {
      const currentAuth = useStore.getState().cardAuth
      const originatingAddress = currentAuth.activeAddress || _cardWalletAddress
      if (!originatingAddress) {
        currentAuth.clearTokens()
        throw new Error('Authentication failed')
      }

      const refreshRequest = {
        address: originatingAddress,
        accessToken: currentAuth.accessToken || '',
        expiresIn: currentAuth.expiresIn ? Math.floor(currentAuth.expiresIn / 1000) : 0,
        tokenType: currentAuth.tokenType || 'Bearer',
      }

      const trackingId = getCardTrackingId()

      const response = await fetch(
        '/api/card/auth/refresh',
        withTrackingId(
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(refreshRequest),
          },
          trackingId,
        ),
      )

      if (!response.ok) {
        if (response.status === 401) {
          useStore.getState().cardAuth.clearTokens()
          throw new Error('Authentication failed')
        }

        const errorData = await response.json().catch(() => null)
        const errorMessage =
          errorData?.data?.error ||
          errorData?.error ||
          errorData?.message ||
          `Token refresh failed: ${response.status} ${response.statusText}`
        throw new Error(errorMessage)
      }

      const refreshedTokens = await response.json()
      const tokenData = refreshedTokens.data

      if (!tokenData || !tokenData.accessToken || !tokenData.tokenType || !tokenData.expiresIn) {
        throw new Error('Invalid token response from server')
      }

      const latestAuth = useStore.getState().cardAuth
      const currentAddress = latestAuth.activeAddress || _cardWalletAddress
      if (originatingAddress && currentAddress && originatingAddress !== currentAddress) {
        throw new Error('Authentication failed')
      }

      if (_cardWalletAddress && !latestAuth.activeAddress) {
        latestAuth.setActiveAddress(_cardWalletAddress)
      }

      latestAuth.setTokens({ ...tokenData, expiresIn: toAbsoluteExpiryMs(tokenData.expiresIn) })

      return {
        accessToken: tokenData.accessToken,
        tokenType: tokenData.tokenType,
      }
    } finally {
      _refreshPromise = null
    }
  })()

  return _refreshPromise
}

/**
 * Smart fetch function that automatically handles authentication and token refresh
 * Automatically detects card API endpoints and applies authentication
 */
export const createSmartFetch =
  (url: string, options: RequestInit = {}) =>
  async (variables: any, authHeaders?: Record<string, string>) => {
    // Build headers with card-specific headers for card API endpoints
    const headers = new Headers(authHeaders ?? { 'Content-Type': 'application/json' })

    // Add card-specific headers and auth if this is a card API endpoint
    if (isCardAPIEndpoint(url)) {
      // Get fresh tokens (will refresh if needed)
      const freshTokens = await getFreshTokens()

      headers.set('Authorization', `Bearer ${freshTokens.accessToken}`)
    }

    const requestBody = options.body || (variables ? JSON.stringify(variables) : undefined)
    new Headers(options.headers).forEach((value, key) => {
      headers.set(key, value)
    })

    let response: Response
    try {
      const trackingId = getCardTrackingId()
      const requestURL = getCardAPIRequestURL(url)

      response = await fetch(
        requestURL,
        withTrackingId(
          {
            ...options,
            headers,
            body: requestBody,
          },
          trackingId,
        ),
      )
    } catch (networkError) {
      // Handle network errors (CORS, connection issues, etc.)
      throw new Error(
        `Network error: ${networkError instanceof Error ? networkError.message : 'Unknown network error'}`,
      )
    }

    if (!response.ok) {
      try {
        const errorData = await response.json()
        const errorMessage =
          errorData.data?.error ||
          errorData.error ||
          errorData.message ||
          `Request failed: ${response.status} ${response.statusText}`
        const errorCode = errorData.data?.code || errorData.code || ''
        const fullMessage = errorCode ? `[${errorCode}] ${errorMessage}` : errorMessage
        throw new Error(fullMessage)
      } catch (parseError) {
        if (
          parseError instanceof Error &&
          !(parseError instanceof SyntaxError) &&
          parseError.message &&
          !parseError.message.includes('Request failed:')
        ) {
          throw parseError
        }

        throw new Error(`Request failed: ${response.status} ${response.statusText}`)
      }
    }

    // Handle empty responses (like 204 No Content)
    const contentLength = response.headers.get('content-length')
    const contentType = response.headers.get('content-type')

    if (contentLength === '0' || response.status === 204) {
      // Return empty success response for no content
      return {}
    }

    if (contentType?.includes('application/json')) {
      return response.json()
    }

    // If response has content but isn't JSON, try to parse as JSON anyway (fallback)
    const text = await response.text()
    if (text.trim() === '') {
      return {}
    }

    try {
      return JSON.parse(text)
    } catch {
      // If it's not JSON, return the text wrapped in an object
      return { message: text }
    }
  }

/**
 * Smart GET request function that automatically handles authentication and token refresh
 * Automatically detects card API endpoints and applies authentication
 */
export const createSmartGet =
  (url: string, options: RequestInit = {}) =>
  async (authHeaders?: Record<string, string>) => {
    // Build headers with card-specific headers for card API endpoints
    const headers = new Headers(authHeaders ?? { 'Content-Type': 'application/json' })

    // Add card-specific headers and auth if this is a card API endpoint
    if (isCardAPIEndpoint(url)) {
      // Get fresh tokens (will refresh if needed)
      const freshTokens = await getFreshTokens()

      headers.set('Authorization', `Bearer ${freshTokens.accessToken}`)
    }

    new Headers(options.headers).forEach((value, key) => {
      headers.set(key, value)
    })

    let response: Response
    try {
      const trackingId = getCardTrackingId()
      const requestURL = getCardAPIRequestURL(url)

      response = await fetch(
        requestURL,
        withTrackingId(
          {
            ...options,
            method: 'GET',
            headers,
          },
          trackingId,
        ),
      )
    } catch (networkError) {
      // Handle network errors (CORS, connection issues, etc.)
      throw new Error(
        `Network error: ${networkError instanceof Error ? networkError.message : 'Unknown network error'}`,
      )
    }

    if (!response.ok) {
      try {
        const errorData = await response.json()
        const errorMessage =
          errorData.data?.error ||
          errorData.error ||
          errorData.message ||
          `Request failed: ${response.status} ${response.statusText}`
        const errorCode = errorData.data?.code || errorData.code || ''
        const fullMessage = errorCode ? `[${errorCode}] ${errorMessage}` : errorMessage
        throw new Error(fullMessage)
      } catch (parseError) {
        if (
          parseError instanceof Error &&
          !(parseError instanceof SyntaxError) &&
          parseError.message &&
          !parseError.message.includes('Request failed:')
        ) {
          throw parseError
        }
        throw new Error(`Request failed: ${response.status} ${response.statusText}`)
      }
    }

    // Handle empty responses (like 204 No Content)
    const contentLength = response.headers.get('content-length')
    const contentType = response.headers.get('content-type')

    if (contentLength === '0' || response.status === 204) {
      // Return empty success response for no content
      return {}
    }

    if (contentType?.includes('application/json')) {
      return response.json()
    }

    // If response has content but isn't JSON, try to parse as JSON anyway (fallback)
    const text = await response.text()
    if (text.trim() === '') {
      return {}
    }

    try {
      return JSON.parse(text)
    } catch {
      // If it's not JSON, return the text wrapped in an object
      return { message: text }
    }
  }
